import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { UserEmail } from '../../hooks/useUserEmails';

interface EditEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: UserEmail | null;
  workExperiences: Array<{ id: string; company: string; position: string; }>;
  onSave: (emailId: string, updates: { label?: string; work_experience_id?: string | null }) => Promise<void>;
}

export function EditEmailModal({ isOpen, onClose, email, workExperiences, onSave }: EditEmailModalProps) {
  const [label, setLabel] = useState('');
  const [workExperienceId, setWorkExperienceId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (email) {
      setLabel(email.label || '');
      setWorkExperienceId(email.work_experience_id || '');
      setError('');
    }
  }, [email]);

  const handleSave = async () => {
    if (!email) return;

    setIsSaving(true);
    setError('');

    try {
      const updates: { label?: string; work_experience_id?: string | null } = {
        work_experience_id: workExperienceId || null
      };
      
      // Only update label for non-primary emails
      if (!email.is_primary) {
        updates.label = label || undefined;
      }
      
      await onSave(email.id, updates);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update email');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-md rounded-lg bg-white dark:bg-gray-800 p-6">
          <div className="flex items-center justify-between mb-4">
            <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
              {email?.is_primary ? 'Default Work Experience' : 'Edit Email Settings'}
            </Dialog.Title>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {email && (
            <>
              <div className="mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  Email: {email.email}
                </p>
              </div>

              {!email.is_primary && (
                <div className="mb-4">
                  <label htmlFor="label" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Label
                  </label>
                  <select
                    id="label"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">No label</option>
                    <option value="Work">Work</option>
                    <option value="Personal">Personal</option>
                    <option value="School">School</option>
                    <option value="Project">Project</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              )}

              {workExperiences.length > 0 && (
                <div className="mb-4">
                  <label htmlFor="workExperience" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Link to Work Experience
                  </label>
                  <select
                    id="workExperience"
                    value={workExperienceId}
                    onChange={(e) => setWorkExperienceId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">None</option>
                    {workExperiences.map((exp) => (
                      <option key={exp.id} value={exp.id}>
                        {exp.position} at {exp.company}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {email.is_primary 
                      ? 'Set a default work experience for notes sent from your primary email.'
                      : 'Notes sent from this email will be automatically linked to this work experience.'}
                  </p>
                </div>
              )}

              {error && (
                <div className="mb-4 text-sm text-red-600 dark:text-red-400">
                  {error}
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}